import type { ShapeCommittedUndoMeta } from '@/lib/shapes/types/shape-committed-undo-meta'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { textOnPathConfigsEqual } from '@/lib/shapes/text-on-path-session'

export type ShapeUndoDiff = {
  before: Partial<ShapeData>
  after: Partial<ShapeData>
  /** Whether anything undoable changed — `handleShapeCommitted` pushes no history row otherwise. */
  hasHistory: boolean
  /** Freeze state flipped; the caller resyncs the active-object mirror. */
  freezeStateChanged: boolean
}

/**
 * Reduces a shape commit's undo meta to the before / after patches its history row records: only
 * the fields that actually changed (positions and dimensions past a small epsilon), with the
 * companion fields each change needs to replay (path data with sides / inset, and so on).
 */
export function diffShapeUndoMeta(undo: ShapeCommittedUndoMeta): ShapeUndoDiff {
  const EPS_POS = 1e-4
  const before: Partial<ShapeData> = {}
  const after: Partial<ShapeData> = {}
  let hasHistory = false
  let freezeStateChanged = false

  if (
    typeof undo.before.rotation === 'number' &&
    typeof undo.after.rotation === 'number' &&
    undo.before.rotation !== undo.after.rotation
  ) {
    before.rotation = undo.before.rotation
    after.rotation = undo.after.rotation
    hasHistory = true
  }

  const xPosChanged =
    typeof undo.before.x === 'number' &&
    typeof undo.after.x === 'number' &&
    Math.abs(undo.before.x - undo.after.x) > EPS_POS
  const yPosChanged =
    typeof undo.before.y === 'number' &&
    typeof undo.after.y === 'number' &&
    Math.abs(undo.before.y - undo.after.y) > EPS_POS

  if (xPosChanged || yPosChanged) {
    if (xPosChanged) {
      before.x = undo.before.x
      after.x = undo.after.x
    }

    if (yPosChanged) {
      before.y = undo.before.y
      after.y = undo.after.y
    }

    hasHistory = true
  }

  const EPS_DIM = 1e-4

  const widthChanged =
    typeof undo.before.width === 'number' &&
    typeof undo.after.width === 'number' &&
    Math.abs(undo.before.width - undo.after.width) > EPS_DIM
  const heightChanged =
    typeof undo.before.height === 'number' &&
    typeof undo.after.height === 'number' &&
    Math.abs(undo.before.height - undo.after.height) > EPS_DIM

  if (widthChanged || heightChanged) {
    if (widthChanged) {
      before.width = undo.before.width
      after.width = undo.after.width
    }

    if (heightChanged) {
      before.height = undo.before.height
      after.height = undo.after.height
    }

    hasHistory = true
  }

  if (undo.before.pathCommands != null && undo.after.pathCommands != null) {
    before.pathCommands = undo.before.pathCommands
    after.pathCommands = undo.after.pathCommands
    if (undo.before.path != null) before.path = undo.before.path
    if (undo.after.path != null) after.path = undo.after.path
    hasHistory = true
  }

  // Crop mode's frame resize, pan, rotate-photo, zoom slider, and flip buttons all commit a
  // full replacement `photoFill` (see handleCropFrameCommit/handlePhotoPanCommit in
  // center-canvas/hooks/use-crop-gesture-handlers.ts) but never touched this generic diff
  // before — so those edits enqueued to the API fine but never pushed anything onto the undo
  // stack, and Ctrl+Z silently no-opped (or, for a frame resize where width/height also
  // changed, reverted the frame size while leaving the photo's pan/scale at its post-gesture
  // value).
  const photoFillChanged =
    undo.before.photoFill != null &&
    undo.after.photoFill != null &&
    (Math.abs(undo.before.photoFill.offsetX - undo.after.photoFill.offsetX) > EPS_POS ||
      Math.abs(undo.before.photoFill.offsetY - undo.after.photoFill.offsetY) > EPS_POS ||
      Math.abs(undo.before.photoFill.scale - undo.after.photoFill.scale) > EPS_POS ||
      (undo.before.photoFill.rotation ?? 0) !== (undo.after.photoFill.rotation ?? 0) ||
      !!undo.before.photoFill.flippedX !== !!undo.after.photoFill.flippedX ||
      !!undo.before.photoFill.flippedY !== !!undo.after.photoFill.flippedY ||
      (undo.before.photoFill.pezId ?? '') !== (undo.after.photoFill.pezId ?? '') ||
      (undo.before.photoFill.previewUrl ?? '') !== (undo.after.photoFill.previewUrl ?? ''))

  if (photoFillChanged) {
    before.photoFill = undo.before.photoFill
    after.photoFill = undo.after.photoFill
    hasHistory = true
  }

  if (undo.before.parentGroupId !== undo.after.parentGroupId) {
    before.parentGroupId = undo.before.parentGroupId
    after.parentGroupId = undo.after.parentGroupId
    hasHistory = true
  }

  if (undo.before.locked !== undo.after.locked) {
    before.locked = undo.before.locked
    after.locked = undo.after.locked
    hasHistory = true
  }

  if (undo.before.frozen !== undo.after.frozen) {
    before.frozen = undo.before.frozen
    after.frozen = undo.after.frozen
    hasHistory = true
    freezeStateChanged = true
  }

  const EPS_EFFECT = 1e-6
  const roundedChanged =
    typeof undo.before.roundedCorner === 'number' &&
    typeof undo.after.roundedCorner === 'number' &&
    Math.abs(undo.before.roundedCorner - undo.after.roundedCorner) > EPS_EFFECT

  if (roundedChanged) {
    before.roundedCorner = undo.before.roundedCorner
    after.roundedCorner = undo.after.roundedCorner
    hasHistory = true
  }

  const sidesChanged =
    typeof undo.before.sides === 'number' &&
    typeof undo.after.sides === 'number' &&
    undo.before.sides !== undo.after.sides

  if (sidesChanged) {
    before.sides = undo.before.sides
    after.sides = undo.after.sides
    if (undo.before.pathCommands != null) before.pathCommands = undo.before.pathCommands
    if (undo.after.pathCommands != null) after.pathCommands = undo.after.pathCommands
    if (undo.before.kindId !== undefined) before.kindId = undo.before.kindId
    if (undo.after.kindId !== undefined) after.kindId = undo.after.kindId
    hasHistory = true
  }

  const insetChanged =
    typeof undo.before.inset === 'number' &&
    typeof undo.after.inset === 'number' &&
    undo.before.inset !== undo.after.inset

  if (insetChanged) {
    before.inset = undo.before.inset
    after.inset = undo.after.inset
    if (undo.before.pathCommands != null) before.pathCommands = undo.before.pathCommands
    if (undo.after.pathCommands != null) after.pathCommands = undo.after.pathCommands
    if (undo.before.kindId !== undefined) before.kindId = undo.before.kindId
    if (undo.after.kindId !== undefined) after.kindId = undo.after.kindId
    hasHistory = true
  }

  const opacityChanged =
    typeof undo.before.opacity === 'number' &&
    typeof undo.after.opacity === 'number' &&
    Math.abs(undo.before.opacity - undo.after.opacity) > EPS_EFFECT

  if (opacityChanged) {
    before.opacity = undo.before.opacity
    after.opacity = undo.after.opacity
    hasHistory = true
  }

  const shapeTextChanged =
    undo.before.paragraphs != null &&
    undo.after.paragraphs != null &&
    JSON.stringify(undo.before.paragraphs) !== JSON.stringify(undo.after.paragraphs)

  if (shapeTextChanged) {
    before.paragraphs = undo.before.paragraphs
    after.paragraphs = undo.after.paragraphs
    hasHistory = true
  }

  // WMPCM-9514. Deliberately NOT guarded on both sides being non-null, the way the
  // `paragraphs` check above is: the two transitions that matter here are ADDING text on a
  // path (before is undefined) and CLEARING it (after is undefined), and a `!= null` guard
  // would make exactly those two non-undoable. `applyPartialShapeUpdate` is a spread, so a
  // patch of `{ textOnPath: undefined }` really does clear the key on the way back.
  //
  // WMPCM-9554: asked through `textOnPathConfigsEqual`, the same helper the commit itself
  // gates on (`commitAndExitTextOnPathMode`), rather than a second comparison written here.
  // Two independently written definitions of "changed" is how a commit that writes ends up
  // recording no undo entry, or an undo entry gets pushed for a write that never happened.
  // The `JSON.stringify` pair this replaces was also field-ORDER sensitive, so a config
  // rebuilt in a different order compared unequal while being value-identical — a Ctrl+Z
  // that restores nothing and reads as dead.
  const textOnPathChanged = !textOnPathConfigsEqual(undo.before.textOnPath, undo.after.textOnPath)

  if (textOnPathChanged) {
    before.textOnPath = undo.before.textOnPath
    after.textOnPath = undo.after.textOnPath
    hasHistory = true
  }

  // Only fields both sides carry: a commit whose `before` is a full snapshot and `after` a partial
  // patch must not read as a fill change.
  const fillFieldChanged = (key: 'fill' | 'fillColorId' | 'fillTint') =>
    key in undo.before && key in undo.after && undo.before[key] !== undo.after[key]
  const fillChanged =
    fillFieldChanged('fill') || fillFieldChanged('fillColorId') || fillFieldChanged('fillTint')

  if (fillChanged) {
    // All three together, so undo restores a None fill (no id) as well as a palette one.
    before.fill = undo.before.fill
    before.fillColorId = undo.before.fillColorId
    before.fillTint = undo.before.fillTint
    after.fill = undo.after.fill
    after.fillColorId = undo.after.fillColorId
    after.fillTint = undo.after.fillTint
    hasHistory = true
  }

  return { before, after, hasHistory, freezeStateChanged }
}
